import React from "react";
import { couplingEnforcement, TYPE_LABELS } from "../domain/deviceCoupling";

/**
 * CoupledDevicesField -- the "Coupled devices" picker on the device form.
 *
 * A checkbox per couplable device on the same meter (the candidates are
 * chosen by the caller from deviceSelectors.listCouplingCandidates, so only
 * supported pairs ever appear here). Ticking one records the link; saving
 * writes it on both devices (deviceMutations).
 *
 * Under the list, one note per partner kind says whether the optimizer will
 * enforce the link with the strategies as currently set. When enforcement
 * lives on this device's own strategy field the note carries a button that
 * sets that strategy; it is a button, not an automatic change, because a
 * coupling and a strategy are different decisions and the form never makes
 * the second one for the user.
 *
 * @param {{
 *   deviceType: "battery"|"ev"|"solar"|"generator",
 *   candidates: Array<{ device_id: string, type: string, name: string }>,
 *   selectedIds: string[],
 *   strategies: { charging_strategy?: string, discharging_strategy?: string },
 *   onChange: (ids: string[]) => void,
 *   onSetStrategy: (field: string, value: string) => void,
 * }} props
 */
export default function CoupledDevicesField({
  deviceType,
  candidates = [],
  selectedIds = [],
  strategies = {},
  onChange,
  onSetStrategy,
}) {
  const selected = new Set(selectedIds);
  const partners = candidates.filter((c) => selected.has(c.device_id));
  const notes = couplingEnforcement(deviceType, strategies, partners);

  const toggle = (id) => {
    const next = selected.has(id)
      ? selectedIds.filter((x) => x !== id)
      : [...selectedIds, id];
    onChange(next);
  };

  return (
    <div className="add-device-form-group" data-testid="coupled-devices-field">
      <label htmlFor="coupled_device_ids">Coupled Devices:</label>
      <div
        id="coupled_device_ids"
        role="group"
        aria-label="Coupled devices"
        style={{
          border: "1px solid #e2e8f0",
          borderRadius: "6px",
          padding: "8px 10px",
          maxHeight: "160px",
          overflowY: "auto",
        }}
      >
        {candidates.length === 0 ? (
          <span style={{ fontSize: "13px", color: "#64748b" }}>
            No device on this meter can be coupled to this one yet.
          </span>
        ) : (
          candidates.map((c) => (
            <label
              key={c.device_id}
              htmlFor={`coupled-${c.device_id}`}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "13px",
                fontWeight: 400,
                margin: "2px 0",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                id={`coupled-${c.device_id}`}
                checked={selected.has(c.device_id)}
                onChange={() => toggle(c.device_id)}
              />
              <span>{c.name}</span>
              <span style={{ color: "#94a3b8" }}>
                {TYPE_LABELS[c.type] || c.type}
              </span>
            </label>
          ))
        )}
      </div>
      <span style={{ fontSize: "12px", color: "#64748b" }}>
        A coupled device is wired to this one directly (a generator into a
        battery or EV, or a battery built into an EV&apos;s charger). The link
        is saved on both devices.
      </span>
      {notes.map((note) => (
        <div
          key={note.partnerType}
          role="note"
          data-testid={`coupling-note-${note.partnerType}`}
          style={{
            marginTop: "6px",
            padding: "6px 8px",
            borderRadius: "4px",
            fontSize: "12px",
            background: note.enforced ? "#f0fdf4" : "#fffbeb",
            color: note.enforced ? "#166534" : "#92400e",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "8px",
          }}
        >
          <span>{note.message}</span>
          {note.suggestion && onSetStrategy && (
            <button
              type="button"
              onClick={() =>
                onSetStrategy(note.suggestion.field, note.suggestion.value)
              }
              style={{
                whiteSpace: "nowrap",
                fontSize: "12px",
                padding: "2px 8px",
                border: "1px solid #92400e",
                borderRadius: "4px",
                background: "white",
                color: "#92400e",
                cursor: "pointer",
              }}
            >
              {note.suggestion.label}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
